import { div, a, iframe } from './util'

const PROCESSED_MARKER = 'processed'

interface EmbedMessageData {
  videoID?: number
  backlinkUrl?: string
  linkAsNoFollow?: boolean
  videoTitle?: string
  videoDuration?: string
}

export class VideoEmbed {
  private scriptTag: HTMLScriptElement
  private embedContainer: HTMLElement

  static embedAll(): void {
    VideoEmbed.unprocessedScripts.forEach((script) => {
      new VideoEmbed(script as HTMLScriptElement).addToDOMAndListen()
    })
  }

  constructor(scriptTag: HTMLScriptElement) {
    this.scriptTag = scriptTag
    this.embedContainer = this.buildEmbed()
  }

  addToDOMAndListen(): void {
    this.insertEmbed()
    this.listenToIFrameEvents()
    this.scriptTag.setAttribute(PROCESSED_MARKER, 'true')
  }

  private static get unprocessedScripts(): Element[] {
    return [...document.querySelectorAll(`script[data-id]:not([${PROCESSED_MARKER}])`)]
  }

  private buildEmbed(): HTMLElement {
    const container = div('embed-container')
    container.appendChild(div('iframe-container')).appendChild(this.buildIFrame())
    return container
  }

  private insertEmbed(): void {
    this.targetNode.parentNode!.replaceChild(this.embedContainer, this.targetNode)
  }

  private listenToIFrameEvents(): void {
    window.addEventListener(
      'message',
      (event: MessageEvent<EmbedMessageData>) => {
        this.addBacklinkComponent(event)
      },
      false,
    )
  }

  private addBacklinkComponent({ origin, data }: MessageEvent<EmbedMessageData>): void {
    if (origin === this.domain && data.videoID === this.videoId && data.backlinkUrl) {
      const backlink = a('video-backlink', { href: data.backlinkUrl })
      if (data.linkAsNoFollow) {
        backlink.setAttribute('rel', 'nofollow')
      }
      backlink.appendChild(div('video-backlink--play-logo'))
      backlink.appendChild(div('video-backlink--title', { textContent: data.videoTitle }))
      backlink.appendChild(
        div('video-backlink--duration', { textContent: `(${data.videoDuration})` }),
      )
      backlink.appendChild(div('video-backlink--logo'))

      this.embedContainer.appendChild(backlink)
    }
  }

  private get iFrameSrc(): string {
    const parentDomain = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`
    const src = `${this.domain}/embed?id=${this.videoId}&parent_domain=${parentDomain}`
    return this.partnerKey ? `${src}&partner_key=${this.partnerKey}` : src
  }

  private get domain(): string {
    return this.scriptTag.dataset.origin || 'https://studyflix.de'
  }

  private get videoId(): number {
    return parseInt(this.scriptTag.dataset.id!)
  }

  private get partnerKey(): string | undefined {
    return this.scriptTag.dataset.partner
  }

  private get targetNode(): Element {
    return document.querySelector(this.scriptTag.dataset.targetDiv!) || this.scriptTag
  }

  private buildIFrame(): HTMLElement {
    return iframe('iframe', {
      id: 'video-embed',
      name: 'video-embed',
      allowfullscreen: true,
      scrolling: 'no',
      src: this.iFrameSrc,
    })
  }
}
